/** Ventana horaria visible en el scheduler (eje horizontal): 00:00–24:00 */
export const SCHEDULER_HOUR_START = 0;
export const SCHEDULER_HOUR_END = 24;

function pad2(n) {
  return String(n).padStart(2, '0');
}

export function toDateKey(date) {
  if (typeof date === 'string' && date.length >= 10) return date.slice(0, 10);
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Lunes de la semana que contiene `anchor` */
export function startOfWeekMonday(anchor) {
  const d =
    typeof anchor === 'string' && anchor.length >= 10
      ? new Date(`${anchor.slice(0, 10)}T12:00:00`)
      : new Date(anchor);
  if (Number.isNaN(d.getTime())) return startOfWeekMonday(new Date());
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function buildWeekDays(weekMonday) {
  return Array.from({ length: 7 }, (_, i) => {
    const date = addDays(weekMonday, i);
    return {
      date,
      dateKey: toDateKey(date),
      label: date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' }),
      labelFull: date.toLocaleDateString('es-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'short',
      }),
    };
  });
}

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function minutesFromMidnight(date) {
  return date.getHours() * 60 + date.getMinutes();
}

function combineDateAndTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [h, m] = timeStr.split(':').map(Number);
  if (!y || !mo || !d || Number.isNaN(h) || Number.isNaN(m)) return null;
  return new Date(y, mo - 1, d, h, m, 0);
}

function isOvernightShift(start, end) {
  return toDateKey(start) !== toDateKey(end);
}

function formatClock(date) {
  return date.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function schedulerDayStart(dateKey) {
  return combineDateAndTime(dateKey, `${pad2(SCHEDULER_HOUR_START)}:00`);
}

function schedulerDayEnd(dateKey) {
  if (SCHEDULER_HOUR_END >= 24) {
    const end = combineDateAndTime(dateKey, '23:59');
    if (end) end.setSeconds(59, 999);
    return end;
  }
  return combineDateAndTime(dateKey, `${pad2(SCHEDULER_HOUR_END)}:00`);
}

/** Límites reales del tramo visible en un día (para solape y etiqueta). */
function buildSegmentMeta(rangeStart, rangeEnd, segmentDateKey) {
  const startKey = toDateKey(rangeStart);
  const endKey = toDateKey(rangeEnd);
  const overnight = isOvernightShift(rangeStart, rangeEnd);
  const fullLabel = `${formatClock(rangeStart)} – ${formatClock(rangeEnd)}`;

  if (overnight && segmentDateKey === startKey) {
    const segmentStart = rangeStart;
    const segmentEnd = schedulerDayEnd(startKey);
    if (!segmentEnd) return null;
    return {
      segmentStart,
      segmentEnd,
      timeLabel: fullLabel,
    };
  }

  if (overnight && segmentDateKey === endKey) {
    const segmentStart = schedulerDayStart(endKey);
    const segmentEnd = rangeEnd;
    if (!segmentStart || !segmentEnd || segmentEnd <= segmentStart) return null;
    return {
      segmentStart,
      segmentEnd,
      timeLabel: `${formatClock(segmentStart)} – ${formatClock(segmentEnd)}`,
    };
  }

  return {
    segmentStart: rangeStart,
    segmentEnd: rangeEnd,
    timeLabel: fullLabel,
  };
}

/** Posición % en el día para bloques horarios. `segmentDateKey` acota turnos que cruzan medianoche. */
export function blockTimePosition(start, end, segmentDateKey = null) {
  const windowStart = SCHEDULER_HOUR_START * 60;
  const windowEnd = SCHEDULER_HOUR_END * 60;
  const total = windowEnd - windowStart;

  const startKey = toDateKey(start);
  const endKey = toDateKey(end);
  const overnight = isOvernightShift(start, end);

  let startMin = minutesFromMidnight(start);
  let endMin = minutesFromMidnight(end);

  if (overnight && segmentDateKey === startKey) {
    endMin = windowEnd;
  } else if (overnight && segmentDateKey === endKey) {
    startMin = windowStart;
    endMin = minutesFromMidnight(end);
    if (endMin <= windowStart) return null;
  } else if (endMin <= startMin) {
    endMin = windowEnd;
  }

  const clampedStart = Math.max(windowStart, Math.min(startMin, windowEnd - 15));
  const clampedEnd = Math.max(clampedStart + 15, Math.min(endMin, windowEnd));
  const left = ((clampedStart - windowStart) / total) * 100;
  const width = ((clampedEnd - clampedStart) / total) * 100;
  return { left, width: Math.max(width, 4) };
}

export function formatTimeRange(start, end) {
  const fmt = (d) =>
    d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${fmt(start)} – ${fmt(end)}`;
}

function resolveResource(ev) {
  const p = ev.extendedProps || {};
  if (p.patternId != null) {
    return {
      id: `pattern-${p.patternId}`,
      label: p.patternName || `Patrón ${p.patternId}`,
    };
  }
  if (p.entityLabel) {
    return {
      id: `entity-${p.entityId ?? p.entityLabel}`,
      label: String(p.entityLabel),
    };
  }
  if (p.view === 'factory') {
    return { id: 'factory-calendar', label: 'Calendario de planta' };
  }
  return { id: 'schedule', label: 'Horario' };
}

function eventDateKey(ev) {
  if (ev.allDay) {
    return typeof ev.start === 'string' ? ev.start.slice(0, 10) : toDateKey(ev.start);
  }
  const start = toDate(ev.start);
  return start ? toDateKey(start) : '';
}

function eventRange(ev) {
  if (ev.allDay) return { start: null, end: null, allDay: true };
  const start = toDate(ev.start);
  const end = toDate(ev.end);
  if (!start) return null;
  return { start, end: end && end > start ? end : start, allDay: false };
}

/**
 * Convierte eventos FullCalendar → modelo scheduler (recursos + bloques en semana).
 */
export function buildSchedulerModel(events, weekMonday) {
  const weekDays = buildWeekDays(weekMonday);
  const weekKeys = new Set(weekDays.map((d) => d.dateKey));
  const resourceMap = new Map();
  const rawBlocks = [];

  (events || []).forEach((ev) => {
    const dateKey = eventDateKey(ev);
    const range = eventRange(ev);
    const endDateKey =
      range && !range.allDay && range.end ? toDateKey(range.end) : dateKey;
    if (!weekKeys.has(dateKey) && !weekKeys.has(endDateKey)) return;

    const resource = resolveResource(ev);
    resourceMap.set(resource.id, resource);
    const p = ev.extendedProps || {};
    const color = ev.backgroundColor || ev.borderColor || '#2563eb';

    if (!range || range.allDay) {
      if (!weekKeys.has(dateKey)) return;
      rawBlocks.push({
        id: ev.id || `${resource.id}-${dateKey}-allday`,
        resourceId: resource.id,
        dateKey,
        allDay: true,
        title: ev.title || '—',
        timeLabel: 'Todo el día',
        color,
        isWorkingDay: p.isWorkingDay !== false,
        isHoliday: Boolean(p.isRegisteredHoliday || p.holidayName),
        holidayPayload:
          p.isRegisteredHoliday || p.holidayName
            ? {
                id: p.holidayId ?? null,
                name: p.holidayName || ev.title,
                date: dateKey,
              }
            : null,
      });
      return;
    }

    const startKey = toDateKey(range.start);
    const overnight = isOvernightShift(range.start, range.end);
    const baseTimedBlock = {
      resourceId: resource.id,
      allDay: false,
      title: p.shiftName || ev.title || 'Turno',
      color,
      start: range.start,
      end: range.end,
      isWorkingDay: true,
      isHoliday: false,
      holidayPayload: null,
    };

    const pushSegment = (segmentDateKey, suffix) => {
      const meta = buildSegmentMeta(range.start, range.end, segmentDateKey);
      if (!meta) return;
      const position = blockTimePosition(range.start, range.end, segmentDateKey);
      if (!position) return;
      rawBlocks.push({
        ...baseTimedBlock,
        id: `${ev.id || resource.id}-${segmentDateKey}-${suffix}`,
        dateKey: segmentDateKey,
        segmentStart: meta.segmentStart,
        segmentEnd: meta.segmentEnd,
        timeLabel: meta.timeLabel,
        position,
      });
    };

    if (overnight) {
      if (weekKeys.has(startKey)) pushSegment(startKey, 'evening');
      if (weekKeys.has(endDateKey)) pushSegment(endDateKey, 'morning');
      return;
    }

    if (!weekKeys.has(dateKey)) return;

    const meta = buildSegmentMeta(range.start, range.end, dateKey);
    rawBlocks.push({
      ...baseTimedBlock,
      id: ev.id || `${resource.id}-${dateKey}-${range.start.getTime()}`,
      dateKey,
      segmentStart: meta?.segmentStart ?? range.start,
      segmentEnd: meta?.segmentEnd ?? range.end,
      timeLabel: meta?.timeLabel ?? formatTimeRange(range.start, range.end),
      position: blockTimePosition(range.start, range.end),
    });
  });

  const resources = Array.from(resourceMap.values()).sort((a, b) =>
    a.label.localeCompare(b.label, 'es')
  );

  const blocksByCell = new Map();
  rawBlocks.forEach((block) => {
    const key = `${block.resourceId}|${block.dateKey}`;
    if (!blocksByCell.has(key)) blocksByCell.set(key, []);
    blocksByCell.get(key).push(block);
  });

  const blocks = [];
  blocksByCell.forEach((cellBlocks) => {
    assignLanesInCell(cellBlocks).forEach((block) => blocks.push(block));
  });

  return { resources, blocks, weekDays };
}

function blockVisualRange(block) {
  if (block.allDay) return null;
  const start = block.segmentStart ?? block.start;
  const end = block.segmentEnd ?? block.end;
  if (!start || !end) return null;
  return { start, end };
}

/** Dos bloques se solapan en el mismo día/recurso */
function blocksOverlap(a, b) {
  if (a.allDay && b.allDay) return true;
  if (a.allDay || b.allDay) return true;
  const ar = blockVisualRange(a);
  const br = blockVisualRange(b);
  if (!ar || !br) return false;
  return ar.start < br.end && br.start < ar.end;
}

/**
 * Asigna carril (lane) solo cuando hay solape horario.
 * Turnos seguidos del mismo patrón (06–14, 14–22, 22–06) comparten lane 0.
 */
function assignLanesInCell(cellBlocks) {
  const sorted = [...cellBlocks].sort((a, b) => {
    if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
    const as = (a.segmentStart ?? a.start)?.getTime() ?? 0;
    const bs = (b.segmentStart ?? b.start)?.getTime() ?? 0;
    return as - bs;
  });

  const laneGroups = [];

  sorted.forEach((block) => {
    let lane = 0;
    for (; lane < laneGroups.length; lane += 1) {
      const hasOverlap = laneGroups[lane].some((other) => blocksOverlap(block, other));
      if (!hasOverlap) break;
    }
    if (!laneGroups[lane]) laneGroups[lane] = [];
    laneGroups[lane].push(block);
    block.lane = lane;
  });

  return sorted;
}

export function getHourColumns() {
  const cols = [];
  for (let h = SCHEDULER_HOUR_START; h < SCHEDULER_HOUR_END; h += 1) {
    cols.push({
      hour: h,
      label: `${pad2(h)}:00`,
    });
  }
  return cols;
}

/** Bloques de un recurso en un día concretos (vista día horizontal) */
export function blocksForResourceDay(blocks, resourceId, dateKey) {
  return blocks
    .filter((b) => b.resourceId === resourceId && b.dateKey === dateKey)
    .sort((a, b) => (a.lane ?? 0) - (b.lane ?? 0));
}
